import { NUSDIcon, WhiteCheckMarkIcon } from "../../../AssetComponents/Images";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, memo } from "react";
import { api } from "../../../Components/functions/Api";
import { useLocation, useNavigate } from "react-router-dom";
import { getTime, getDay } from "../../../Components/functions/Functions";
import { getProfileFromWallet } from "../../../Components/functions/Functions";
import Skeleton from "react-loading-skeleton";
import { updateTokenSafe } from "../../../Components/functions/Functions";
import { Mainnet } from "@nibiruchain/nibijs";
import { updateNFTStore } from "../../../ReduxSlices/NFTSlice";

// 🚀 PHASE 7: Memoize ChatItemMobile to prevent unnecessary rerenders
export const ChatItemMobile = memo(({
  myChatId,
  time,
  mode,
  setReceiver = () => {},
  setNft = () => {},
  setReceiverProfile = () => {},
  onChatSelect = () => {},
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const account = useSelector((state) => state.auth.account);
  const dispatch = useDispatch();
  const [receiver, setReceiverAccount] = useState(null);
  const [latestMessageTime, setLatestTime] = useState(time);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [nftId, setNftId] = useState(null);
  const diff = useSelector((state) => state.time.diffToUTC);
  const [profile, setProfile] = useState();
  
  const [isLoadingNft, setIsLoadingNft] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [hasNoNftId, setHasNoNftId] = useState(false); // Track if nftId is confirmed missing/invalid
  
  const nft = useSelector((state) => state.nft.nfts[nftId]);

  const mainnet = Mainnet();
  
  const getToken = async () => {
    if (!nftId) {
      console.log("❌ ChatItemMobile: No nftId provided");
      setHasNoNftId(true); // Mark as no NFT if no nftId
      return;
    }
    
    if (nft) {
      console.log("✅ ChatItemMobile: Token already loaded:", nftId);
      return;
    }
    
    if (isLoadingNft) {
      console.log("🔄 ChatItemMobile: Already loading token:", nftId);
      return;
    }
    
    console.log("🔄 ChatItemMobile: Loading token:", nftId);
    
    try {
      setIsLoadingNft(true);
      // 🚀 INSTANT DASHBOARDS: Use backend API instead of RPC calls
      const propertyData = await api("property/getProperty", {
        token_id: nftId,
      });
      if (propertyData) {
        // Update Redux with backend data (no RPC needed)
        dispatch(
          updateNFTStore({
            token_id: nftId,
            metaData: propertyData.metaData,
            short: propertyData.short,
            rentals: propertyData.rentals,
            access: propertyData.access,
          })
        );
        console.log("✅ ChatItemMobile: Token loaded successfully from backend:", nftId);
      } else {
        // Fallback to RPC only if backend fails
        console.log("⚠️ ChatItemMobile: Backend returned no data, falling back to RPC:", nftId);
        const success = await updateTokenSafe(
          {
            token_id: nftId,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
        if (success) {
          console.log("✅ ChatItemMobile: Token loaded successfully from RPC:", nftId);
        } else {
          console.log("❌ ChatItemMobile: Failed to load token:", nftId);
          setHasNoNftId(true);
        }
      }
    } catch (error) {
      console.error("💥 ChatItemMobile: Error loading token:", nftId, error);
      // Try RPC fallback on error
      try {
        await updateTokenSafe(
          {
            token_id: nftId,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
      } catch (rpcError) {
        console.error("💥 ChatItemMobile: RPC fallback also failed:", nftId, rpcError);
        setHasNoNftId(true);
      }
    } finally {
      setIsLoadingNft(false);
    }
  };

  // 🔥 FIX: Always load NFT for chat list preview (needs building name), prioritize active chats
  // Only try to load if we have a valid nftId (not confirmed missing)
  useEffect(() => {
    if (nftId && !hasNoNftId && !nft && !isLoadingNft) {
      if (active) {
        // Load immediately if active (being viewed)
        getToken();
      } else {
        // Load NFT for inactive items too (needed for building name preview in list)
        // Use small delay to prioritize active chat loading, but still load for preview
        const delayTimer = setTimeout(() => {
          getToken();
        }, 200); // Small delay to not block active chat loading
        return () => clearTimeout(delayTimer);
      }
    }
  }, [active, nftId, hasNoNftId, nft, isLoadingNft]);
  
  // 🔥 NEW: Timeout to stop showing skeleton if NFT doesn't load within reasonable time
  useEffect(() => {
    if (nftId && !hasNoNftId && !nft && !isLoadingNft) {
      const timeout = setTimeout(() => {
        console.log("⚠️ ChatItemMobile: NFT load timeout after 10s, setting hasNoNftId flag");
        setHasNoNftId(true);
      }, 10000); // 10 second timeout
      return () => clearTimeout(timeout);
    }
  }, [nftId, hasNoNftId, nft, isLoadingNft]);

  const getProfile = async () => {
    if (!receiver || profile) return;
    
    try {
      setIsLoadingProfile(true);
      const profileData = await getProfileFromWallet(receiver);
      setProfile(profileData);
    } catch (error) {
      console.error(`Failed to load profile for ${receiver}:`, error);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (time !== null) {
      setLatestTime((currentTime) => {
        if (!currentTime) return time;
        
        try {
          const newTime = new Date(time);
          const existingTime = new Date(currentTime);
          
          if (newTime.getTime() > existingTime.getTime()) {
            return time;
          } else {
            return currentTime;
          }
        } catch (error) {
          console.error("Error comparing times:", error);
          return time;
        }
      });
    }
  }, [time]);

  const handleChatSelect = () => {
    if (receiver && nftId) {
      setReceiver(receiver);
      setNft(nftId);
      setReceiverProfile(profile);
      onChatSelect(myChatId, receiver, nftId, profile);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setActive(searchParams.get("chat") === myChatId);
  }, [location.search, myChatId]);

  const getChattingInfo = async () => {
    if (!account || !myChatId) {
      console.log("❌ ChatItemMobile: Missing account or chatId");
      return;
    }
    
    try {
      console.log("🔄 ChatItemMobile: Getting chatting info for:", myChatId);
      const res = await api("chat/getChattingInfo", {
        id: myChatId,
        reader: account,
      });
      
      if (res) {
        console.log("✅ ChatItemMobile: Chatting info received:", {
          receiver: res.receiver,
          nftId: res.nftId,
          unreadMessages: res.unreadMessages
        });
        
        setUnreadMessages(res.unreadMessages || 0);
        
        if (res.latestMessageTime) {
          setLatestTime(res.latestMessageTime);
        }
        
        setReceiverAccount(res.receiver);
        
        if (res.nftId && res.nftId !== 'undefined' && res.nftId !== 'null') {
          setNftId(res.nftId);
          setHasNoNftId(false); // We have a valid nftId
        } else {
          console.log("⚠️ ChatItemMobile: Invalid or missing nftId received:", res.nftId);
          setHasNoNftId(true); // Mark that there's no valid nftId
          setNftId(null); // Ensure nftId is null
        }
      } else {
        console.log("❌ ChatItemMobile: No response from getChattingInfo");
      }
    } catch (error) {
      console.error("💥 ChatItemMobile: Failed to get chatting info:", error);
    }
  };

  useEffect(() => {
    if (myChatId && account) {
      getChattingInfo();
    }
  }, [account, myChatId]);

  // 🔥 FIX: Always load profile for chat list (needed for avatar/name), prioritize active chats
  useEffect(() => {
    if (receiver && !profile && !isLoadingProfile) {
      if (active) {
        // Load immediately if active
        getProfile();
      } else {
        // Load profile for inactive items too (needed for avatar/name in list)
        // Use small delay to prioritize active chat loading
        const delayTimer = setTimeout(() => {
          getProfile();
        }, 100); // Small delay to not block active chat loading
        return () => clearTimeout(delayTimer);
      }
    }
  }, [active, receiver, profile, isLoadingProfile]);

  const hasValidNftData = nft && nft.metaData && nft.short;
  const pricePerDay = hasValidNftData ? nft.short.price_per_day : null;
  const buildingName = hasValidNftData ? nft.metaData.buildingName : null;
  const propertyImage = hasValidNftData && nft.metaData.images?.length > 0 ? nft.metaData.images[0] : null;

  return (
    <div
      onClick={handleChatSelect}
      className={`cursor-pointer bg-white p-[2px] rounded-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] ${
        active ? 'ring-2 ring-[#5b1dee]' : ''
      }`}
    >
      <div className="bg-white hover:bg-[#f6f6f6] rounded-[10px] w-full h-full p-[12px]">
        <div className="w-full justify-between flex items-start">
          <div className="flex items-center gap-[4px] w-max py-[4px] px-[8px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
            <div className="p-[4px] rounded-full bg-[#5DBE89] w-max">
              <WhiteCheckMarkIcon className="w-[12px]" />
            </div>
            <div className="text-[#5DBE89] text-[14px]">Inquiry</div>
          </div>
          
          <div className="flex flex-col items-end gap-[2px]">
            {unreadMessages > 0 && (
              <div className="bg-[#5b1dee] text-white text-xs rounded-full w-[20px] h-[20px] flex items-center justify-center">
                {unreadMessages > 9 ? '9+' : unreadMessages}
              </div>
            )}
            
            <div className="flex items-center gap-[4px]">
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.41768 2.76525C9.86093 1.33875 13.8082 2.9745 15.2347 6.41775C16.6612 9.861 15.0254 13.8083 11.5822 15.2348C8.13893 16.6613 4.19168 15.0255 2.76518 11.5823C1.33943 8.139 2.97443 4.19175 6.41768 2.76525"
                  stroke="url(#paint0_linear_457_25163)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.78906 5.98874V9.47699L11.5311 11.1487"
                  stroke="url(#paint1_linear_457_25163)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_457_25163"
                    x1="9.00009"
                    y1="2.24976"
                    x2="9.00009"
                    y2="15.7502"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#6B349A" />
                    <stop offset="1" stopColor="#4C37C3" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_457_25163"
                    x1="10.1601"
                    y1="5.98874"
                    x2="10.1601"
                    y2="11.1487"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#6B349A" />
                    <stop offset="1" stopColor="#4C37C3" />
                  </linearGradient>
                </defs>
              </svg>

              <div className="text-sm">
                {latestMessageTime ? (
                  (() => {
                    try {
                      const messageDate = new Date(latestMessageTime);
                      if (isNaN(messageDate.getTime())) {
                        return "Invalid Time";
                      }
                      return getDay(messageDate) + " " + getTime(messageDate);
                    } catch (error) {
                      console.error("Error formatting time:", error);
                      return "Invalid Time";
                    }
                  })()
                ) : (
                  <Skeleton count={1} height={16} width={80} />
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-[10px] items-center mt-[8px]">
          {!isLoadingProfile && profile?.Avatar ? (
            <div className="p-[2px] w-[40px] h-[40px] rounded-full bg-gradient-to-b from-[#5b1deec0] from-0% to-70% to-[#5b1dee00]">
              <img
                alt="Avatar"
                className="w-full h-full rounded-full object-cover"
                src={profile.Avatar}
              />
            </div>
          ) : (
            <Skeleton
              width={40}
              height={40}
              style={{ borderRadius: "100px" }}
            />
          )}
          
          {!isLoadingProfile && profile?.Name ? (
            <div className="text-base truncate flex-1">
              {profile.Name.replace("/", " ")}
            </div>
          ) : (
            <Skeleton count={1} height={16} width={80} />
          )}
        </div>

        <div className="flex justify-between w-full items-end mt-[8px]">
          <div className="flex-1 min-w-0">
            {hasNoNftId ? (
              // Show empty space when no NFT data (e.g., traveler without listings)
              <div className="font-normal text-[14px] truncate">
                {/* Empty space - no property information */}
              </div>
            ) : !isLoadingNft && hasValidNftData && nft.metaData.buildingName ? (
              <div className="font-normal text-[14px] truncate">
                {nft.metaData.buildingName}
              </div>
            ) : isLoadingNft ? (
              <>
                <Skeleton height={8} width="80%" />
                <Skeleton height={8} width="60%" />
              </>
            ) : (
              // Empty space when loading finished but no data
              <div className="font-normal text-[14px] truncate">
                {/* Empty space - no property data */}
              </div>
            )}

            <div className="flex items-center gap-[4px] mt-2">
              <NUSDIcon />
              {hasNoNftId ? (
                // Empty space for price when no NFT
                <div className="text-sm"></div>
              ) : !isLoadingNft && pricePerDay !== null && pricePerDay !== undefined ? (
                <div className="text-sm">
                  {(
                    Number(pricePerDay) /
                    10 ** process.env.REACT_APP_USDC_DECIMALS
                  ).toFixed(6).replace(/\.?0+$/, '')}{" "}
                  USDC
                </div>
              ) : isLoadingNft ? (
                <Skeleton height={16} width={60} />
              ) : (
                // Empty space when loading finished but no data
                <div className="text-sm"></div>
              )}
            </div>
          </div>
          
          {hasNoNftId ? (
            // Empty space when no NFT - don't show placeholder
            null
          ) : !isLoadingNft && hasValidNftData && nft.metaData.images?.[0] ? (
            <img
              alt="Property"
              src={nft.metaData.images[0]}
              className="rounded-[12px] w-[60px] h-[44px] object-cover flex-shrink-0"
            />
          ) : isLoadingNft ? (
            <Skeleton 
              height={44} 
              width={60} 
              style={{ borderRadius: "12px" }}
            />
          ) : (
            // Empty space when loading finished but no data - don't show placeholder
            null
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // 🚀 PHASE 7: Custom comparison to prevent unnecessary rerenders
  // Only rerender if these props change
  return (
    prevProps.myChatId === nextProps.myChatId &&
    prevProps.time === nextProps.time &&
    prevProps.mode === nextProps.mode
  );
});
